import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { TranslateService } from '@ngx-translate/core';
import { NotificationRef } from '@progress/kendo-angular-notification';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { NavigationTopBarModule } from './navigation-top-bar/navigation-top-bar.module';
import { RoutingBreadCrumbsComponent } from './routing/routing-breadcrumbs.component';
import { LoadingPanelComponent } from './shared/components/loading-panel';
import { MaintenanceDialogComponent } from './shared/components/maintenance-dialog/maintenance-dialog/maintenance-dialog.component';
import { SecurityService } from './shared/security/security.service';
import { isPortalUserAccessDenied } from './shared/security/portal-access-error';
import { IdleService } from './shared/services/idle/idle.service';
import { KendoAlertService } from './shared/services/kendo-alerts.service';
import { CustomLanguageService } from './shared/services/language.service';
import { MaintenanceService } from './shared/services/maintenance/maintenance.service';
import { PortalAccessService } from './shared/services/portal-access/portal-access.service';
import { CompanyAdminConfigurationStore } from './shared/stores/company-admin-configuration.store';
import { UserSettingsStore } from './shared/stores/user-settings.store';

@Component({
  selector: 'ntw-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet, NavigationTopBarModule, MaintenanceDialogComponent, RoutingBreadCrumbsComponent, LoadingPanelComponent]
})
export class AppComponent implements OnInit, OnDestroy {
  title = "Customer Portal";
  loading = true;
  isIframe = false;

  public isMaintenanceDialogOpened = false;

  public closeMaintenanceDialog = () => {
    this.isMaintenanceDialogOpened = false;
  }

  private _securityObject: AccountInfo = null;
  private readonly _destroying$ = new Subject<void>();
  private authorizationLoadVersion = 0;
  private authorizationAccountId: string | null = null;

  public get securityObject(): AccountInfo {
    return this._securityObject;
  }
  public set securityObject(accountInfo: AccountInfo) {
    const previousAccountId = this._securityObject?.localAccountId;
    const nextAccountId = accountInfo?.localAccountId;
    this._securityObject = accountInfo;
    this.securityService.setUser(accountInfo);

    if (previousAccountId !== nextAccountId) {
      this.securityService.setCurrentAccount(null);
    }

    if (!accountInfo) {
      this.authorizationAccountId = null;
      ++this.authorizationLoadVersion;
      this.securityService.setPortalAccess(null);
      return;
    }

    if (this.authorizationAccountId === accountInfo.localAccountId) {
      return;
    }

    this.authorizationAccountId = accountInfo.localAccountId;
    const loadVersion = ++this.authorizationLoadVersion;
    this.securityService.$authorizationStartupInProgress.next(true);
    this.portalAccessService.get().pipe(
      take(1),
      takeUntil(this._destroying$)
    ).subscribe({
      next: access => {
        if (loadVersion !== this.authorizationLoadVersion) {
          return;
        }
        this.securityService.setPortalAccess(access);
        this.setLanguageFromSettings(accountInfo.localAccountId);
        this.securityService.$authorizationStartupInProgress.next(false);
        if (this.router.url === '/security') {
          this.router.navigate(['/']);
        }
      },
      error: error => {
        if (loadVersion !== this.authorizationLoadVersion) {
          return;
        }
        this.securityService.setPortalAccess(null);
        this.securityService.$authorizationStartupInProgress.next(false);
        if (error?.status >= 500) {
          this.kendoAlertService.showErrorAlert(error);
        }
        if (!isPortalUserAccessDenied(error)) {
          this.router.navigate(['/security']);
        }
      }
    });
  }

  isMaintenanceActive = false;

  constructor(
    private securityService: SecurityService,
    private translateService: TranslateService,
    private languageService: CustomLanguageService,
    private msalBroadcastService: MsalBroadcastService,
    private userSettingsStore: UserSettingsStore,
    private authService: MsalService,
    private idleService: IdleService,
    private kendoAlertService: KendoAlertService,
    private router: Router,
    private companyAdminConfigurationStore: CompanyAdminConfigurationStore,
    private maintenanceService: MaintenanceService,
    private portalAccessService: PortalAccessService,
  ) { }

  ngOnInit(): void {
    this.companyAdminConfigurationStore.isLoading().subscribe({
      next: isLoading => {
        const isAdminConfigLoaded = this.companyAdminConfigurationStore.isLoaded();
        this.loading = isLoading && !isAdminConfigLoaded;
      }
    });
    this.maintenanceService.isAnnouncementActive.subscribe({
      next: isAnnouncementActive => {
        if (isAnnouncementActive) {
          this.isMaintenanceDialogOpened = true;
        }
      }
    });
    this.maintenanceService.isMaintenanceActive.subscribe({
      next: isMaintenanceActive => {
        this.isMaintenanceActive = isMaintenanceActive;
        if (isMaintenanceActive) {
          this.isMaintenanceDialogOpened = true;
        }
      }
    });

    this.isIframe = window !== window.parent && !window.opener;
    this.translateService.use(this.languageService.currentLanguage);
    this.handleMsalEvents();

    this.setupTimeout();
  }

  ngOnDestroy(): void {
    this._destroying$.next(null);
    this._destroying$.complete();
  }

  private handleMsalEvents() {
    this.msalBroadcastService.inProgress$.pipe(
      takeUntil(this._destroying$)
    )
    .subscribe((status: InteractionStatus) => {
      if (status === InteractionStatus.None) {
        this.securityObject = this.authService.instance.getActiveAccount();
      }
    });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.authService.instance.setActiveAccount(payload.account);
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        console.error('login failed');
        console.error(result);
        this.authService.loginRedirect();
      });
  }

  private setLanguageFromSettings(userB2CId: string) {
    this.userSettingsStore.load(userB2CId).subscribe({
      next: userSettings => {
        if (!userSettings) {
          this.languageService.currentLanguage = this.languageService.browserLanguage;
        }
        else if (userSettings?.languageCode && userSettings.languageCode != this.languageService.currentLanguage) {
          this.languageService.currentLanguage = userSettings.languageCode;
        }
      },
      error: error => {
        this.kendoAlertService.showErrorAlert(error);
      }
    });
  }

  private logoutNotification?: NotificationRef;

  private setupTimeout() {
    this.idleService.onIdleEnd.pipe(takeUntil(this._destroying$)).subscribe(() => {
      this.logoutNotification?.afterHide.subscribe(() => {
        this.logoutNotification = undefined;
        this.kendoAlertService.showSuccessAlert(this.translateService.instant('idle.welcomeBack'), true, 2000);
      });
      this.logoutNotification?.hide();
    });

    this.idleService.onTimeoutWarning.pipe(takeUntil(this._destroying$)).subscribe((countdown) => {
      if (!this.logoutNotification) {
        this.logoutNotification = this.kendoAlertService.showWarningAlert(this.getUntilTimeoutMessage(countdown));
      }
      else {
        this.logoutNotification.notification.instance.templateString = this.getUntilTimeoutMessage(countdown);
      }
    });

    this.idleService.onTimeout.pipe(takeUntil(this._destroying$)).subscribe(() => {
      if (this.logoutNotification) {
        this.logoutNotification.notification.instance.templateString = this.translateService.instant('idle.timeout');
      }
      else {
        this.kendoAlertService.showWarningAlert(this.translateService.instant('idle.timeout'));
      }
      this.idleService.watch();
      this.securityService.reset();
      this.authService.logout();
    });

    this.idleService.watch();
  }

  private getUntilTimeoutMessage(timeoutAfter: number) {
    let message = this.translateService.instant('idle.idle');

    if (timeoutAfter > 60) {
      message += ` ${Math.floor(timeoutAfter / 60)} ${this.translateService.instant('idle.minutes')}`;

      if (timeoutAfter % 60 > 0) {
        message += ` ${timeoutAfter % 60} ${this.translateService.instant('idle.seconds')}`;
      }
      message += '.';
    }
    else {
      message += ` ${timeoutAfter} ${this.translateService.instant('idle.seconds')}.`;
    }

    return message;
  }
}
